import { useEffect } from 'react';
import moment from 'moment';
import Link from 'next/link';
import { useDispatch, useSelector } from 'react-redux';
import { color, Common } from '@stockbit/ui';

import If from 'global/ErrorHandlers/If';
import Image from 'global/Image';
import { getLastSlashURL } from 'utils/general';
import { effects, selectors } from '../slice';

import {
  AnnouncementAttachmentLabel,
  AnnouncementCompanyCard,
  AnnouncementCompanyCardIcon,
  AnnouncementLoading,
  AttachmentList,
  Modal,
  SymbolErrorIcon,
} from './styled';

const { Text, Flex } = Common;

const Loading = () => (
  <Flex
    style={{ minHeight: '310px', width: '100%' }}
    justify="center"
    align="center"
  >
    <AnnouncementLoading>
      <Image src="/icon/announcement-loading.svg" />
    </AnnouncementLoading>
  </Flex>
);

interface ReportItemProps {
  attachment: string;
  company_icon_url: string;
  headline: string;
  name: string;
  retrieved_on: string;
  symbol: string;
  title: string;
}

interface ModalAnnounchementProps {
  url: string;
  visible: boolean;
  destroyOnClose?: boolean;
  onCancel?: () => void;
}

function ModalAnnouncement({
  url,
  visible = false,
  destroyOnClose,
  onCancel,
}: ModalAnnounchementProps) {
  const postId = getLastSlashURL(url);
  const dispatch = useDispatch();

  const { announcementReports } = useSelector(selectors);
  const { isLoading } = announcementReports;

  useEffect(() => {
    if (visible && postId) {
      // @ts-ignore
      dispatch(effects.getAnnouncementReports(postId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, postId, visible]);

  const currentPost: ReportItemProps[] = announcementReports[postId] || [];
  const postedDate = currentPost[0]?.retrieved_on || moment();
  const companyIcon = currentPost[0]?.company_icon_url;

  return (
    <Modal
      visible={visible}
      centered
      footer={null}
      width="520px"
      onCancel={onCancel}
      destroyOnClose={destroyOnClose}
    >
      <If condition={isLoading}>
        <Loading />
      </If>
      <If condition={!isLoading}>
        <Flex justify="center" mb="24px" mt="18px">
          <Image src="/icon/announcement.svg" alt="announcement icon" />
        </Flex>
        <Flex mb="8px" justify="center" align="center">
          <Text
            size="18px"
            family="bold"
            align="center"
            style={{ maxWidth: '85%' }}
          >
            {currentPost[0]?.headline}
          </Text>
        </Flex>
        <Text height="19px" mb="24px" align="center">
          {moment(postedDate).format('DD MMMM YYYY, HH:mm')}
        </Text>
        <Link href={`/symbol/${currentPost[0]?.symbol}`}>
          <a
            style={{ color: 'unset' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <AnnouncementCompanyCard>
              <AnnouncementCompanyCardIcon>
                <If condition={companyIcon}>
                  <Image src={currentPost[0]?.company_icon_url} />
                </If>
                <If condition={!companyIcon}>
                  <SymbolErrorIcon>
                    {currentPost[0]?.symbol?.toUpperCase()?.slice(0, 1)}
                  </SymbolErrorIcon>
                </If>
              </AnnouncementCompanyCardIcon>
              <Flex direction="column" style={{ gap: '2px', flex: 1 }}>
                <Text size="16px" height="15px" family="bold">
                  {currentPost[0]?.symbol}
                </Text>
                <Text height="15px" color={color.highgray}>
                  {currentPost[0]?.name}
                </Text>
              </Flex>
              <Flex align="center">
                <Image src="/icon/chevron-right-gray.svg" />
              </Flex>
            </AnnouncementCompanyCard>
          </a>
        </Link>
        {currentPost.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <AnnouncementAttachmentLabel color={color.highgray}>
              Attachment:
            </AnnouncementAttachmentLabel>
            <AttachmentList>
              {currentPost.map((report: ReportItemProps, index: number) => (
                <li key={index}>
                  <a
                    href={report.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Flex padding="8px 0">
                      <Image
                        src="/icon/announcement-report.svg"
                        alt="icon file"
                        width="24"
                        height="20"
                      />

                      <Text size="14px" height="18.9px" padding="0 12px">
                        {report.title}
                      </Text>
                    </Flex>
                  </a>
                </li>
              ))}
            </AttachmentList>
          </div>
        )}
      </If>
    </Modal>
  );
}

export default ModalAnnouncement;
